import React, { useState, useEffect } from "react";
import { Check, Star, Zap, Shield, Rocket, Info, MessageCircle, RefreshCw, AlertCircle, Users, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_annual: number;
  features: string[];
  icon_name: string;
  is_recommended: boolean;
  badge_text: string | null;
  is_active: boolean;
  user_limit: number;
}

const iconMap: Record<string, React.ReactNode> = {
  Shield: <Shield className="w-6 h-6 text-primary-100" />,
  Star: <Star className="w-6 h-6 text-yellow-400" />,
  Zap: <Zap className="w-6 h-6 text-orange-400" />,
  Rocket: <Rocket className="w-6 h-6 text-purple-400" />,
};

const PlanosPage = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [supportLink, setSupportLink] = useState("https://wa.me/5500000000000");

  const loadData = async () => {
    setLoading(true);
    const [plansRes, settingsRes] = await Promise.all([
      supabase.from("plans_config").select("*").eq("is_active", true).order("display_order", { ascending: true }),
      supabase.from("app_settings").select("*").eq("key", "support_link").single()
    ]);

    if (plansRes.data) setPlans(plansRes.data);
    if (settingsRes.data) setSupportLink(settingsRes.data.value);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSelectPlan = async (plan: Plan) => {
    // Se for o plano Turbo ou Profissional (geralmente o último ou o que tiver Zap icon), 
    // redirecionamos para o suporte
    if (plan.icon_name === "Zap" || plan.name.toLowerCase().includes("turbo") || plan.name.toLowerCase().includes("consultor")) {
      window.open(supportLink, "_blank");
      return;
    }

    setLoadingPlan(plan.id);
    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-checkout', {
        body: { planId: plan.id, isAnnual }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        // Redireciona para o checkout do Stripe
        window.location.href = data.url;
      } else {
        throw new Error("Resposta inválida do servidor.");
      }
    } catch (err: any) {
      console.error(err);
      toast({ title: "Erro ao processar", description: err.message || "Erro ao conectar com o provedor de pagamentos.", variant: "destructive" });
      setLoadingPlan(null);
    }
  };

  const calculatePrice = (plan: Plan) => {
    const price = isAnnual ? plan.price_annual / 12 : plan.price_monthly;
    const priceStr = price.toFixed(2);
    const [main, cents] = priceStr.split('.');
    
    return {
      main,
      cents,
      total: isAnnual ? plan.price_annual.toFixed(2) : plan.price_monthly.toFixed(2),
      savings: isAnnual ? (plan.price_monthly * 12 - plan.price_annual).toFixed(2) : "0"
    };
  };

  const averageSavings = plans.length > 0 
    ? Math.round(plans.reduce((acc, p) => acc + (1 - (p.price_annual / (p.price_monthly * 12))), 0) / plans.length * 100)
    : 45;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="size-8 text-primary animate-spin" />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-white space-y-4">
        <AlertCircle className="size-12 text-amber-500" />
        <h2 className="text-2xl font-bold">Nenhum plano configurado</h2>
        <p className="text-white/60">Acesse o painel administrativo para criar seus planos de serviço.</p>
        <Button onClick={() => navigate("/login")}>Acessar Painel</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col items-center py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-blue-50/30 to-white">
      {/* Botão Voltar */}
      <div className="absolute top-8 left-8 z-20">
        <button 
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-slate-400 hover:text-primary transition-all duration-300 font-bold text-sm uppercase tracking-widest"
        >
          <div className="p-2 rounded-full bg-white border border-slate-200 shadow-sm group-hover:border-primary/30 group-hover:shadow-md transition-all">
            <ArrowLeft className="size-4" />
          </div>
          <span className="hidden sm:inline">Voltar</span>
        </button>
      </div>
      {/* Background blobs for Liquid Glass effect (Light Version) */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[5%] w-[40%] h-[50%] bg-primary/5 rounded-full blur-[80px]" />
        <div className="absolute top-[20%] -right-[5%] w-[30%] h-[40%] bg-blue-400/5 rounded-full blur-[100px]" />
        <div className="absolute -bottom-[10%] left-[20%] w-[50%] h-[50%] bg-primary-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto text-center mb-10 relative z-10">
        <Badge variant="outline" className="mb-4 py-1 px-4 border-primary/20 bg-primary/5 text-primary-700 uppercase tracking-[0.2em] font-black text-[10px]">
          Tabela de Investimento
        </Badge>
        <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">
          Planos Adaptados à <span className="text-primary">Sua Demanda</span>
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto px-4 font-medium">
          Escolha o nível de processamento ideal para sua análise técnica de dados e otimize seus resultados.
        </p>

        <div className="mt-8 flex items-center justify-center gap-6">
          <button 
            onClick={() => setIsAnnual(false)}
            className={`text-sm font-bold uppercase tracking-wider transition-all ${!isAnnual ? "text-slate-900" : "text-slate-400 hover:text-slate-600"}`}
          >
            Mensal
          </button>
          <div className="relative p-1 bg-slate-100 border border-slate-200 rounded-full flex items-center gap-1 shadow-inner">
            <div 
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-primary rounded-full transition-transform duration-300 shadow-md ${isAnnual ? "translate-x-full" : "translate-x-0"}`}
            />
            <button onClick={() => setIsAnnual(false)} className="relative z-10 w-12 h-6 flex items-center justify-center" aria-label="Mensal" />
            <button onClick={() => setIsAnnual(true)} className="relative z-10 w-12 h-6 flex items-center justify-center" aria-label="Anual" />
          </div>
          <button 
            onClick={() => setIsAnnual(true)}
            className={`text-sm font-bold uppercase tracking-wider flex items-center gap-3 transition-all ${isAnnual ? "text-slate-900" : "text-slate-400 hover:text-slate-600"}`}
          >
            Anual
            <Badge variant="secondary" className="bg-emerald-500 text-white border-none text-[10px] font-black px-2 py-0.5 shadow-sm">
              -{averageSavings}% OFF
            </Badge>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch relative z-10 px-4">
        {plans.map((plan, index) => {
          const prices = calculatePrice(plan);
          const isTurbo = plan.icon_name === "Zap" || plan.name.toLowerCase().includes("turbo");
          
          return (
            <div
              key={plan.id}
              className={`relative flex flex-col p-8 transition-all duration-500 border rounded-[2.5rem] h-full ${
                plan.is_recommended 
                  ? "bg-white border-primary/30 shadow-[0_20px_50px_rgba(26,102,204,0.15)] scale-[1.03] z-10" 
                  : "bg-white/60 backdrop-blur-md border-slate-200 shadow-[0_10px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.06)]"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {plan.badge_text && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-black tracking-widest uppercase shadow-md ${
                  plan.is_recommended ? "bg-primary text-white" : "bg-slate-800 text-white"
                }`}>
                  {plan.badge_text}
                </div>
              )}

              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className={`text-2xl font-black ${plan.is_recommended ? "text-primary" : "text-slate-900"} tracking-tight`}>
                    {plan.name.replace(/auditoria/gi, 'Inspeção')}
                  </h3>
                  <p className="text-[11px] text-slate-400 mt-1 leading-tight font-bold uppercase tracking-wider">{plan.description}</p>
                </div>
                <div className={`p-3 rounded-2xl ${plan.is_recommended ? "bg-primary/5 text-primary" : "bg-slate-50 text-slate-400"} border border-current/10 shrink-0`}>
                  {iconMap[plan.icon_name] || <Shield className="w-6 h-6" />}
                </div>
              </div>

              <div className="mb-8">
                <div key={isAnnual ? 'annual' : 'monthly'} className="flex items-baseline gap-1">
                  <span className="text-slate-400 text-lg font-bold">R$</span>
                  <span className="text-5xl font-black text-slate-900 tracking-tighter">{prices.main}</span>
                  <span className="text-2xl font-black text-slate-900/80">,{prices.cents}</span>
                  <span className="text-slate-400 text-[11px] font-bold ml-1 uppercase">/mês*</span>
                </div>
                {isAnnual && (
                  <div className="mt-2 text-[11px] text-slate-500 font-semibold italic">
                    equivalente a R$ {prices.total} /ano
                    <span className="text-emerald-600 block not-italic font-black mt-0.5">Economia de R$ {prices.savings}</span>
                  </div>
                )}
                
                <div className="mt-5 flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100 w-fit">
                  <Users className="size-4 text-slate-400" />
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    {(plan.user_limit ?? 1) >= 999 ? "Usuários Ilimitados" : `Até ${plan.user_limit ?? 1} usuário(s)`}
                  </span>
                </div>
              </div>

              <Button
                disabled={loadingPlan === plan.id}
                onClick={() => handleSelectPlan(plan)}
                className={`w-full py-7 rounded-2xl text-sm font-black uppercase tracking-[0.2em] transition-all duration-300 shadow-lg ${
                  isTurbo
                    ? "bg-slate-900 hover:bg-slate-800 text-white"
                    : "bg-primary hover:bg-primary-600 text-white shadow-primary/20"
                }`}
              >
                {loadingPlan === plan.id ? "Processando..." : (isTurbo ? "Falar com Consultor" : "Começar Agora")}
              </Button>

              <div className="mt-10 space-y-4 flex-grow">
                {plan.features.map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-center gap-3">
                    <div className={`size-5 rounded-full ${plan.is_recommended ? "bg-primary/10" : "bg-slate-100"} flex items-center justify-center shrink-0`}>
                      <Check className={`size-3 ${plan.is_recommended ? "text-primary" : "text-slate-500"}`} />
                    </div>
                    <span className="text-sm text-slate-600 font-medium tracking-tight">{feature}</span>
                  </div>
                ))}
              </div>

              {plan.is_recommended && (
                <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                  <p className="text-[11px] text-primary font-black uppercase tracking-[0.1em]">O MAIS ESCOLHIDO</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Contact Section */}
      <div className="mt-20 max-w-4xl mx-auto text-center p-10 rounded-[2.5rem] bg-slate-50/50 border border-slate-200/50">
        <h2 className="text-2xl font-black text-slate-900 mb-3">Dúvidas sobre o plano ideal?</h2>
        <p className="text-slate-500 mb-8 font-medium">Nossos especialistas estão prontos para ajudar sua empresa a escalar com segurança.</p>
        <Button 
          variant="outline" 
          className="rounded-2xl px-10 py-6 text-sm font-bold border-slate-200 text-slate-700 hover:bg-white hover:shadow-xl transition-all gap-2"
          onClick={() => window.open(supportLink, "_blank")}
        >
          <MessageCircle className="size-5 text-primary" /> Falar com Suporte no WhatsApp
        </Button>
      </div>
    </div>
  );
};

export default PlanosPage;
